// src/services/frontend-bundler/storage.service.ts
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface BundlerDB extends DBSchema {
  bundles: {
    key: string;
    value: {
      id: string;
      name: string;
      owner: string;
      wallets: any[];
      totalBalance: number;
      isActive: boolean;
      createdAt: Date;
      updatedAt: Date;
      settings: any;
    };
    indexes: { 'by-owner': string; 'by-created': Date };
  };
  wallets: {
    key: string;
    value: {
      id: string;
      bundleId: string;
      address: string;
      encryptedPrivateKey: string;
      balance: number;
      funded: boolean;
      active: boolean;
      createdAt: Date;
      lastUsed?: Date;
    };
    indexes: { 'by-bundle': string; 'by-address': string };
  };
  transactions: {
    key: string;
    value: {
      id: string;
      bundleId: string;
      walletAddress: string;
      signature: string;
      type: 'fund' | 'purchase' | 'sell' | 'withdraw';
      amount: number;
      tokenMint?: string;
      timestamp: Date;
      status: 'pending' | 'confirmed' | 'failed';
    };
    indexes: { 'by-bundle': string; 'by-wallet': string; 'by-timestamp': Date };
  };
  settings: {
    key: string;
    value: any;
  };
}

export class StorageService {
  private db: IDBPDatabase<BundlerDB> | null = null;
  private readonly DB_NAME = 'solana-bundler-db';
  private readonly DB_VERSION = 1;

  async initialize(): Promise<void> {
    if (this.db) return;

    try {
      this.db = await openDB<BundlerDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db) {
          // Create bundles store
          if (!db.objectStoreNames.contains('bundles')) {
            const bundleStore = db.createObjectStore('bundles', { keyPath: 'id' });
            bundleStore.createIndex('by-owner', 'owner');
            bundleStore.createIndex('by-created', 'createdAt');
          }

          // Create wallets store
          if (!db.objectStoreNames.contains('wallets')) {
            const walletStore = db.createObjectStore('wallets', { keyPath: 'id' });
            walletStore.createIndex('by-bundle', 'bundleId');
            walletStore.createIndex('by-address', 'address');
          }

          // Create transactions store
          if (!db.objectStoreNames.contains('transactions')) {
            const txStore = db.createObjectStore('transactions', { keyPath: 'id' });
            txStore.createIndex('by-bundle', 'bundleId');
            txStore.createIndex('by-wallet', 'walletAddress');
            txStore.createIndex('by-timestamp', 'timestamp');
          }

          // Create settings store
          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings');
          }
        },
      });

      console.log('IndexedDB initialized successfully');
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error);
      throw new Error('Failed to initialize storage');
    }
  }

  /**
   * Bundle operations
   */
  async saveBundle(bundle: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const tx = this.db.transaction(['bundles', 'wallets'], 'readwrite');
    
    // Save bundle
    await tx.objectStore('bundles').put(bundle);
    
    // Save wallets separately for better indexing
    for (const wallet of bundle.wallets) {
      await tx.objectStore('wallets').put({
        ...wallet,
        bundleId: bundle.id
      });
    }
    
    await tx.done;
  }

  async getBundle(bundleId: string): Promise<any | null> {
    if (!this.db) throw new Error('Database not initialized');
    
    const bundle = await this.db.get('bundles', bundleId);
    if (!bundle) return null;
    
    // Get wallets for this bundle
    const wallets = await this.db.getAllFromIndex('wallets', 'by-bundle', bundleId);
    bundle.wallets = wallets;
    
    return bundle;
  }

  async updateBundle(bundle: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const tx = this.db.transaction(['bundles', 'wallets'], 'readwrite');
    
    // Update bundle
    await tx.objectStore('bundles').put(bundle);
    
    // Update wallets
    for (const wallet of bundle.wallets) {
      await tx.objectStore('wallets').put({
        ...wallet,
        bundleId: bundle.id
      });
    }
    
    await tx.done;
  }

 async deleteBundle(bundleId: string): Promise<void> {
  if (!this.db) throw new Error('Database not initialized');

  // One atomic readwrite transaction across all stores
  const tx = this.db.transaction(['bundles', 'wallets', 'transactions'], 'readwrite');
  const bundleStore = tx.objectStore('bundles');
  const walletStore = tx.objectStore('wallets');
  const txStore     = tx.objectStore('transactions');

  // Get all wallet keys for this bundle (within the same tx)
  const walletKeys = await walletStore.index('by-bundle').getAllKeys(bundleId);
  for (const key of walletKeys) {
    await walletStore.delete(key);
  }

  // Get all transaction keys for this bundle (within the same tx)
  const txKeys = await txStore.index('by-bundle').getAllKeys(bundleId);
  for (const key of txKeys) {
    await txStore.delete(key);
  }

  // Finally delete the bundle record
  await bundleStore.delete(bundleId);

  // Important: wait for completion
  await tx.done;
}

  async getBundlesByOwner(owner: string): Promise<any[]> {
    if (!this.db) throw new Error('Database not initialized');
    
    const bundles = await this.db.getAllFromIndex('bundles', 'by-owner', owner);
    
    // Load wallets for each bundle
    for (const bundle of bundles) {
      const wallets = await this.db.getAllFromIndex('wallets', 'by-bundle', bundle.id);
      bundle.wallets = wallets;
    }
    
    return bundles;
  }

  /**
   * Transaction operations
   */
  async saveTransaction(transaction: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.add('transactions', transaction);
  }

  async getTransactionsByBundle(bundleId: string): Promise<any[]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('transactions', 'by-bundle', bundleId);
  }

  async getTransactionsByWallet(walletAddress: string): Promise<any[]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAllFromIndex('transactions', 'by-wallet', walletAddress);
  }

  /**
   * Settings operations
   */
  async saveSetting(key: string, value: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('settings', value, key);
  }

  async getSetting(key: string): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('settings', key);
  }

  /**
   * Data migration from localStorage
   */
  async migrateFromLocalStorage(): Promise<void> {
    try {
      // Check if migration already done
      const migrated = await this.getSetting('migrated_from_localstorage');
      if (migrated) return;

      // Migrate bundler wallets
      const bundlerWallets = localStorage.getItem('bundler_wallets');
      const currentBundleId = localStorage.getItem('current_bundle_id');
      
      if (bundlerWallets && currentBundleId) {
        const wallets = JSON.parse(bundlerWallets);
        
        // Create a bundle from old data
        const bundle = {
          id: currentBundleId,
          name: 'Migrated Bundle',
          owner: localStorage.getItem('wallet_address') || 'unknown',
          wallets: wallets.map((w: any, i: number) => ({
            id: `${currentBundleId}-wallet-${i}`,
            address: w.address,
            encryptedPrivateKey: w.privateKey, // Note: This should be encrypted
            balance: w.balance || 0,
            funded: w.funded || false,
            active: w.active !== false,
            createdAt: new Date()
          })),
          totalBalance: wallets.reduce((sum: number, w: any) => sum + (w.balance || 0), 0),
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          settings: {
            autoRebalance: false,
            maxSlippage: 5,
            priorityFee: 0.0001,
            distributeEvenly: true,
            stealthMode: false
          }
        };
        
        await this.saveBundle(bundle);
        console.log('Migrated bundle from localStorage');
      }

      // Mark as migrated
      await this.saveSetting('migrated_from_localstorage', true);
      
      // Optionally clear old localStorage data
      // localStorage.removeItem('bundler_wallets');
      // localStorage.removeItem('current_bundle_id');
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }

  /**
   * Database maintenance
   */
  async clearOldTransactions(daysToKeep: number = 30): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const tx = this.db.transaction('transactions', 'readwrite');
    const transactions = await tx.objectStore('transactions').getAll();
    
    for (const transaction of transactions) {
      if (transaction.timestamp < cutoffDate) {
        await tx.objectStore('transactions').delete(transaction.id);
      }
    }
    
    await tx.done;
  }

  async getDatabaseSize(): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');
    
    // Estimate storage usage
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    }
    
    return 0;
  }

  async exportDatabase(): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');
    
    const bundles = await this.db.getAll('bundles');
    const wallets = await this.db.getAll('wallets');
    const transactions = await this.db.getAll('transactions');
    const settings = await this.db.getAll('settings');
    
    return {
      version: this.DB_VERSION,
      exportedAt: new Date().toISOString(),
      data: {
        bundles,
        wallets,
        transactions,
        settings
      }
    };
  }

  async importDatabase(data: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const tx = this.db.transaction(['bundles', 'wallets', 'transactions', 'settings'], 'readwrite');
    
    // Import bundles
    for (const bundle of data.data.bundles || []) {
      await tx.objectStore('bundles').put(bundle);
    }
    
    // Import wallets
    for (const wallet of data.data.wallets || []) {
      await tx.objectStore('wallets').put(wallet);
    }
    
    // Import transactions
    for (const transaction of data.data.transactions || []) {
      await tx.objectStore('transactions').put(transaction);
    }
    
    // Import settings
    for (const setting of data.data.settings || []) {
      await tx.objectStore('settings').put(setting);
    }
    
    await tx.done;
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

export const storageService = new StorageService();